/**
 * Obtiene una fecha formateada con opciones de visualización.
 * @param {string} date - Fecha en formato ISO.
 * @param {boolean} includeTimeZone - Indica si se debe incluir la zona horaria en el formato.
 * @returns {Object} - Objeto con propiedades de la fecha formateada.
 * @example
 * getFormattedDate('2024-04-13T12:00:00Z', true); // Objeto con propiedades de fecha formateada
 */
export const getFormattedDate = (date, includeTimeZone) => {
  let dateTimeStamp
  try {
    if (includeTimeZone) {
      if (date?.includes('Z')) {
        const dateWithoutTimeZone = date?.split('.')[0]
        dateTimeStamp = new Date(dateWithoutTimeZone)
      } else {
        const dateWithoutTimeZone = date?.substring(0, date.length - 6)
        dateTimeStamp = new Date(dateWithoutTimeZone)
      }
    } else {
      dateTimeStamp = new Date(date)
    }
  } catch (error) {
    dateTimeStamp = new Date(date)
  }

  const weekday = [
    'Domingo',
    'Lunes',
    'Martes',
    'Miércoles',
    'Jueves',
    'Viernes',
    'Sábado',
  ][dateTimeStamp.getDay()]
  const monthday = [
    'Enero',
    'Febrero',
    'Marzo',
    'Abril',
    'Mayo',
    'Junio',
    'Julio',
    'Agosto',
    'Septiembre',
    'Octubre',
    'Noviembre',
    'Diciembre',
  ][dateTimeStamp.getMonth()]

  const addZeroToMinutes = (timeStamp) => {
    return (timeStamp.getMinutes() < 10 ? '0' : '') + timeStamp.getMinutes()
  }

  const addZeroNumberLessThanTen = (number) => {
    return `${number < 10 ? '0' + number : number}`
  }

  const hour = dateTimeStamp.getHours()
  let minutes = dateTimeStamp.getMinutes()
  minutes = addZeroToMinutes(dateTimeStamp)
  let seconds = dateTimeStamp.getSeconds()
  const day = dateTimeStamp.getDate()
  const month = dateTimeStamp.getMonth() + 1
  const year = dateTimeStamp.getFullYear()

  return {
    day,
    hour,
    minutes,
    month,
    monthday,
    seconds,
    weekday,
    year,
    withZero: {
      day: addZeroNumberLessThanTen(day),
      hour: addZeroNumberLessThanTen(hour),
      minutes: minutes,
      month: addZeroNumberLessThanTen(month),
      seconds: addZeroNumberLessThanTen(seconds),
      year: addZeroNumberLessThanTen(year),
    },
  }
}

/**
 * Obtiene la fecha y hora en formato "día/mes/año hora:minutos".
 * @param {string} date - Fecha en formato ISO.
 * @param {boolean} includeTimeZone - Indica si se debe incluir la zona horaria en el formato.
 * @returns {string} - Fecha y hora formateada.
 * @example
 * getDateFormatForNews('2024-04-13T12:00:00Z', true); // "13/04/2024 12:00"
 */
export const getDateFormatForNews = (date, includeTimeZone) => {
  const {
    withZero: { day, hour, minutes, month, year },
  } = getFormattedDate(date, includeTimeZone)

  return `${day}/${month}/${year} ${hour}:${minutes}`
}

/**
 * Obtiene la fecha en formato ISO 8601 (UTC) para ser utilizada en encabezados.
 * @param {string} date - Fecha en formato ISO.
 * @returns {string} - Fecha en formato ISO 8601 (UTC).
 * @example
 * getDateFormatForHead('2024-04-13T12:00:00Z'); // "2024-04-13T12:00:00Z"
 */
export const getDateFormatForHead = (date) => {
  const {
    withZero: { day, hour, minutes, month, seconds, year },
  } = getFormattedDate(date)

  return `${year}-${month}-${day}T${hour}:${minutes}:${seconds}Z`
}

/**
 * Obtiene la fecha y hora en formato ISO (Zulu time).
 * @param {string} date - Fecha en formato ISO.
 * @returns {string} - Fecha y hora en formato ISO.
 * @example
 * getDateFormatZulu('2024-04-13T12:00:00Z'); // "2024-04-13T12:00:00.000Z"
 */
export const getDateFormatZulu = (date) => {
  let dateModified
  try {
    dateModified = new Date(date).toISOString()
  } catch (e) {
    dateModified = ''
  }
  return dateModified
}

/**
 * Obtiene la fecha en formato "día abreviado.mes.año".
 * @param {string} date - Fecha en formato ISO.
 * @returns {string} - Fecha en formato "día abreviado.mes.año".
 * @example
 * getDateFormatDayMonthYear('2024-04-13T12:00:00Z'); // "Jue. 13/04/2024"
 */
export const getDateFormatDayMonthYear = (date) => {
  const {
    weekday,
    withZero: { day, month, year },
  } = getFormattedDate(date)
  return `${weekday.substring(0, 3)}. ${day}/${month}/${year}`
}

/**
 * Obtiene la hora y minutos de una fecha en formato "hora:minutos".
 * @param {string} date - Fecha en formato ISO.
 * @returns {string} - Hora y minutos en formato "hora:minutos".
 * @example
 * getHourMinutes('2024-04-13T12:00:00Z'); // "12:00"
 */
export const getHourMinutes = (date) => {
  const {
    withZero: { hour, minutes },
  } = getFormattedDate(date)
  return `${hour}:${minutes}`
}

/**
 * Obtiene la hora sin cero en los minutos de una fecha en formato "hora:minutos".
 * @param {string} date - Fecha en formato ISO.
 * @returns {string} - Hora sin cero en los minutos en formato "hora:minutos".
 * @example
 * getHourWithoutZeroMinutes('2024-04-13T12:00:00Z'); // "12:0"
 */
export const getHourWithoutZeroMinutes = (date) => {
  const {
    hour,
    withZero: { minutes },
  } = getFormattedDate(date)
  return `${hour}:${minutes}`
}

/**
 * Obtiene la hora y minutos de una fecha en formato "hora:minutos".
 * @param {string} dateString - Cadena de texto con hora y minutos separados por ":".
 * @returns {string} - Hora y minutos en formato "hora:minutos".
 * @example
 * getHourMinutesTwo('12:00'); // "12:00"
 */
export const getHourMinutesTwo = (dateString) => {
  const horaCompleta = dateString
  const partesHora = horaCompleta.split(':')
  const hora = partesHora[0]
  const minutos = partesHora[1]
  const horaFormateada = `${hora}:${minutos}`

  return horaFormateada
}

/**
 * Obtiene el período (AM o PM) de una hora en formato 24 horas.
 * @param {number} hour - Hora en formato 24 horas.
 * @returns {string} - Período AM o PM.
 * @example
 * getPeriod(15); // "PM"
 */
export const getPeriod = (hour) => {
  if (hour >= 12) return 'PM'
  return 'AM'
}

/**
 * Obtiene la hora en formato 12 horas con su período (AM o PM).
 * @param {string} date - Fecha en formato ISO.
 * @returns {string} - Hora en formato 12 horas con su período.
 * @example
 * getHourPmAm('2024-04-13T15:00:00Z'); // "3 PM"
 */
export const getHourPmAm = (date) => {
  const {
    withZero: { hour },
  } = getFormattedDate(date)
  const hourFormat12 = hour % 12 || 12
  return `${hourgetNumberMonthByNameFormat12} ${getPeriod(hour)}`
}

/**
 * Obtiene la hora y minutos en formato 12 horas con su período (AM o PM).
 * @param {string} date - Fecha en formato ISO.
 * @returns {string} - Hora y minutos en formato 12 horas con su período.
 * @example
 * getHourMinutesPmAm('2024-04-13T15:00:00Z'); // "3:00 PM"
 */
export const getHourMinutesPmAm = (date) => {
  const {
    withZero: { hour, minutes },
  } = getFormattedDate(date)
  const hourFormat12 = hour % 12 || 12
  return `${hourFormat12}:${minutes} ${getPeriod(hour)}`
}

/**
 * Formatea la hora en formato AM/PM a partir de milisegundos.
 * @param {number} millisecs - Milisegundos desde la época Unix.
 * @returns {string} - Hora formateada en formato AM/PM.
 * @example
 * formatHourPmAm(1611927600000); // "3 PM"
 */
export const formatHourPmAm = (millisecs) => {
  const dateObj = new Date(millisecs * 1000)
  const options = {
    hour12: true,
    hour: 'numeric',
    timeZone: 'America/Argentina/Buenos_Aires',
  }
  let formattedHour = dateObj.toLocaleTimeString('en-US', options)

  // Elimina los minutos si se muestran
  formattedHour = formattedHour.replace(':00', '')

  return formattedHour
}

/**
 * Formatea la hora y minutos en formato AM/PM a partir de milisegundos.
 * @param {number} millisecs - Milisegundos desde la época Unix.
 * @returns {string} - Hora y minutos formateados en formato AM/PM.
 * @example
 * formatHourMinutesPmAm(1611927600000); // "3:00 PM"
 */
export const formatHourMinutesPmAm = (millisecs) => {
  const dateObj = new Date(millisecs * 1000)
  const options = {
    hour12: true,
    hour: 'numeric',
    minute: '2-digit',
    timeZone: 'America/Argentina/Buenos_Aires',
  }
  let formattedHour = dateObj.toLocaleTimeString('es-AR', options)

  return formattedHour
}

/**
 * Obtiene la fecha actual en formato "día de la semana, día de mes de año".
 * @returns {string} - Fecha actual formateada.
 * @example
 * formatCurrentDateString(); // "jueves, 13 de abril de 2024"
 */
export const formatCurrentDateString = () => {
  const currentDate = new Date()
  const options = { day: 'numeric', month: 'long', year: 'numeric' }
  let formattedDate = currentDate.toLocaleDateString('es-AR', options)
  return formattedDate
}

/**
 * Obtiene el día y mes de una fecha en formato "día-mes".
 * @param {string} date - Fecha en formato ISO.
 * @returns {string} - Día y mes de la fecha.
 * @example
 * getMonthDay('2024-04-13T12:00:00Z'); // "13-04"
 */
export const getMonthDay = (date) => {
  const {
    withZero: { day, month },
  } = getFormattedDate(date)
  return `${day}-${month}`
}

/**
 * Obtiene el día, mes y año de una fecha en formato "día/mes/año".
 * @param {string} date - Fecha en formato ISO.
 * @returns {string} - Día, mes y año de la fecha.
 * @example
 * getMonthDayYear('2024-04-13T12:00:00Z'); // "13/04/2024"
 */
export const getMonthDayYear = (date) => {
  const {
    withZero: { day, month, year },
  } = getFormattedDate(date)
  return `${day}/${month}/${year}`
}

/**
 * Obtiene la fecha del día anterior que no sea fin de semana.
 * @param {Date} date - Fecha para calcular el día anterior.
 * @returns {string} - Fecha del día anterior no fin de semana.
 * @example
 * getPrevDateNotWeekend(new Date('2024-04-15')); // "2024-04-14"
 */
export const getPrevDateNotWeekend = (date = new Date()) => {
  const prevDate = new Date(date)
  prevDate.setDate(prevDate.getDate() - 1)
  let dayOfWeek = prevDate.getDay()
  while (dayOfWeek === 6 || dayOfWeek === 0) {
    prevDate.setDate(prevDate.getDate() - 1)
    dayOfWeek = prevDate.getDay()
  }
  const {
    withZero: { day, month, year },
  } = getFormattedDate(prevDate)
  return `${year}-${month}-${day}`
}

/**
 * Verifica si una fecha es hoy.
 * @param {string} date - Fecha en formato ISO.
 * @returns {boolean} - Verdadero si la fecha es hoy, falso en caso contrario.
 * @example
 * dateIsToday('2024-04-13T12:00:00Z'); // true
 */
export const dateIsToday = (date) => {
  const fecha = getDateFormatForNews(date)
  const currentDate = getDateFormatForNews(new Date())
  return fecha === currentDate ? true : false
}

/**
 * Obtiene la fecha y hora de una publicación.
 * @param {string} date - Fecha en formato ISO.
 * @returns {string} - Fecha y hora formateada de la publicación.
 * @example
 * datePost('2024-04-13T12:00:00Z'); // "13/04/2024 12:00"
 */
export const datePost = (date) => {
  if (date !== undefined && date !== '') {
    const verificationDateIsToday = dateIsToday(date)
    const hour = getHourMinutes(date)
    const dateFormat = getMonthDayYear(date).replace(/\//g, '.')
    return verificationDateIsToday ? hour : dateFormat
  }
}

/**
 * Compara dos fechas para verificar si son iguales en día, mes y año.
 * @param {Date} date1 - Primera fecha a comparar.
 * @param {Date} date2 - Segunda fecha a comparar.
 * @returns {boolean} - Verdadero si las fechas son iguales, falso en caso contrario.
 * @example
 * compareDates(new Date('2024-04-13'), new Date('2024-04-13')); // true
 */
export const compareDates = (date1, date2) => {
  const day1 = date1.getDate()
  const month1 = date1.getMonth()
  const year1 = date1.getFullYear()

  const day2 = date2.getDate()
  const month2 = date2.getMonth()
  const year2 = date2.getFullYear()

  return day1 === day2 && month1 === month2 && year1 === year2
}

/**
 * Obtiene la fecha y hora con día de la semana en formato "Día de la semana, día.mes.año".
 * @param {string} date - Fecha en formato ISO.
 * @returns {string} - Fecha y hora con día de la semana formateada.
 * @example
 * getDateWithDayAndPeriod('2024-04-13T12:00:00Z'); // "Jueves, 13.04.2024"
 */
export const getDateWithDayAndPeriod = (date) => {
  const {
    weekday,
    withZero: { day, month, year },
  } = getFormattedDate(date)
  return `${weekday}, ${day}.${month}.${year}`
}

/**
 * Obtiene la fecha con día de la semana en formato "día.mes.año".
 * @param {string} date - Fecha en formato ISO.
 * @returns {string} - Fecha con día de la semana en formato "día.mes.año".
 * @example
 * getDateWithPeriod('2024-04-13T12:00:00Z'); // "13.04.2024"
 */
export const getDateWithPeriod = (date) => {
  const {
    withZero: { day, month, year },
  } = getFormattedDate(date)
  return `${day}.${month}.${year}`
}

/**
 * Obtiene la fecha y hora en formato para liveblog.
 * @param {string} date - Fecha en formato ISO.
 * @returns {string} - Fecha y hora en formato para liveblog.
 * @example
 * getDateFormatForLiveblog('2024-04-13T12:00:00Z'); // "Jue. 13.04.2024-12:00"
 */
export const getDateFormatForLiveblog = (date) => {
  try {
    const {
      weekday,
      withZero: { day, hour, minutes, month, year },
    } = getFormattedDate(date)

    if (
      !weekday ||
      isNaN(day) ||
      isNaN(month) ||
      isNaN(year) ||
      isNaN(hour) ||
      isNaN(minutes)
    ) {
      return ''
    }

    return `${weekday?.substring(
      0,
      3,
    )}. ${day}.${month}.${year}-${hour}:${minutes}`
  } catch (error) {
    console.error('Error al formatear la fecha:', error)
    return ''
  }
}

/**
 * Obtiene hora en formato para liveblog.
 * @param {string} date - Fecha en formato ISO.
 * @returns {string} - Fecha en formato para highlight liveblog.
 * @example
 * getDateFormatForHighlightLiveblog('2024-04-13T12:00:00Z'); // "13.04.2024"
 */
export const getDateFormatForHighlightLiveblog = (date) => {
  try {
    const {
      withZero: { day, month, year },
    } = getFormattedDate(date)

    if (
      isNaN(day) ||
      isNaN(month) ||
      isNaN(year) 
    ) {
      return ''
    }

    return `${day}.${month}.${year}`
  } catch (error) {
    console.error('Error al formatear la fecha:', error)
    return ''
  }
}

/**
 * Obtiene hora en formato para liveblog.
 * @param {string} date - Fecha en formato ISO.
 * @returns {string} - Hora en formato para highlight liveblog.
 * @example
 * getDateFormatForHighlightLiveblog('2024-04-13T12:00:00Z'); // "12:00"
 */


// Función para evaluar si la fecha es hoy o no
export const getDisplayDate = (rawDate) => {
  if (!rawDate) return null;

  // 1. Obtenemos el string con tu formato original: "Lun. 20.07.2026-12:29"
  const fullFormattedDate = getDateFormatForLiveblog(rawDate);

  // Validamos si falló el formateo o devolvió una fecha inválida
  if (!fullFormattedDate || fullFormattedDate.toString().toLowerCase().includes("invalid")) {
    return null;
  }
  
  // 2. Separamos la fecha de la hora usando el guión
  const parts = fullFormattedDate.split('-');
  
  // Si el formato no tiene guión por algún motivo, devolvemos todo por seguridad
  if (parts.length !== 2) return fullFormattedDate;
  
  const [datePart, timePart] = parts;

  // 3. Obtenemos la fecha de hoy en formato DD.MM.YYYY para compararlo con el string
  const today = new Date();
  const day = String(today.getDate()).padStart(2, '0');
  const month = String(today.getMonth() + 1).padStart(2, '0');
  const year = today.getFullYear();
  const todayString = `${day}.${month}.${year}`; // Ej: "20.07.2026"

  // 4. Si el texto de la fecha (datePart) contiene la fecha de hoy, mostramos solo la hora
  if (datePart.includes(todayString)) {
    return timePart.trim(); // Ej: "12:29"
  }
  
  // 5. Si es de un día anterior, mostramos solo la fecha
  return datePart.trim(); // Ej: "Lun. 20.07.2026"
};


export const getTimeFormatForHighlightLiveblog = (date) => {
  try {
    const {
      withZero: { hour, minutes },
    } = getFormattedDate(date)

    if (
      isNaN(hour) ||
      isNaN(minutes)
    ) {
      return ''
    }

    return `${hour}:${minutes}`
  } catch (error) {
    console.error('Error al formatear la fecha:', error)
    return ''
  }
}


/**
 * Obtiene la fecha para liveblog.
 * @param {string} date - Fecha en formato ISO.
 * @param {boolean} isPreview - Indica si se está en vista previa.
 * @returns {string|null} - Fecha formateada para liveblog o nulo si es vista previa.
 * @example
 * getDateForLiveblog('2024-04-13T12:00:00Z', false); // "2024-04-13T12:00:00Z"
 */
export const getDateForLiveblog = (date, isPreview) => {
  if (isPreview || !date) {
    return null
  }
  const partes = date.split('T')
  const horaMinutosSegundos = partes[1]?.split(':')
  if (!partes[1]) {
    return date
  }
  for (var i = 0; i < horaMinutosSegundos.length; i++) {
    if (horaMinutosSegundos[i].length === 1) {
      horaMinutosSegundos[i] = '0' + horaMinutosSegundos[i]
    }
  }
  const fechaFormateada = partes[0] + 'T' + horaMinutosSegundos.join(':')
  return fechaFormateada
}

/**
 * Verifica si una cadena de texto representa una fecha válida en formato ISO.
 * @param {string} dateString - Cadena de texto con fecha en formato ISO.
 * @returns {boolean} - Verdadero si la cadena es una fecha válida, falso en caso contrario.
 * @example
 * isValidDate('2024-04-13T12:00:00Z'); // true
 */
export const isValidDate = (dateString) => {
  const regex = /^\d{4}-\d{2}-\d{2}T\d{2}:\d{2}:\d{2}$/
  return regex.test(dateString)
}

/**
 * Verifica si una cadena de texto representa una fecha válida para el formato YYYY-MM-DD.
 * @param {string} dateString - Cadena de texto con fecha.
 * @returns {boolean} - Verdadero si la cadena es una fecha válida, falso en caso contrario.
 * @example
 * isValidDate('2024-04-13'); // true
 */
export const isValidSimpleDate = (dateString) => {
  const regex = /^\d{4}-\d{2}-\d{2}$/
  if (!regex.test(dateString)) return false;
  
  const [year, month, day] = dateString.split('-').map(Number);
  const date = new Date(year, month - 1, day);
  return date.getFullYear() === year && 
         date.getMonth() === month - 1 && 
         date.getDate() === day;
}

/**
 * Obtiene el índice del mes a partir de su nombre en español.
 * @param {string} nameMonth - Nombre del mes en español.
 * @returns {number} - Índice del mes.
 * @example
 * getNumberMonthByName('abril'); // 3
 */
export const getNumberMonthByName = (nameMonth) => {
  const months = [
    'enero',
    'febrero',
    'marzo',
    'abril',
    'mayo',
    'junio',
    'julio',
    'agosto',
    'septiembre',
    'octubre',
    'noviembre',
    'diciembre',
  ]
  return months.findIndex((item) => item === nameMonth)
}

/**
 * Obtiene el día de la semana, día de mes y año con formato completo.
 * @param {string} date - Fecha en formato ISO.
 * @returns {string} - Fecha con día de la semana, día de mes y año en formato completo.
 * @example
 * getWeekDayDayMonthDayYear('2024-04-13T12:00:00Z'); // "jueves 13 de abril de 2024"
 */
export const getWeekDayDayMonthDayYear = (date) => {
  const {
    weekday,
    monthday,
    withZero: { day, year },
  } = getFormattedDate(date)

  return `${weekday} ${day} de ${monthday} de ${year}`
}

/**
 * Obtiene el día de la semana, día de mes y año con formato completo.
 * @param {string} date - Fecha en formato ISO.
 * @returns {string} - Fecha con día de la semana, día de mes y año en formato completo.
 * @example
 * getWeekDayDayMonthDayYear('2024-04-13T12:00:00Z'); // "jueves, 13 de abril de 2024"
 */
export const getWeekDayCommaDayMonthDayYear = (date) => {
  const {
    weekday,
    monthday,
    withZero: { day, year },
  } = getFormattedDate(date)

  return `${weekday}, ${day} de ${monthday} de ${year}`
}

/**
 * Obtiene la fecha más reciente entre dos fechas.
 * @param {string} fecha1 - Primera fecha en formato ISO.
 * @param {string} fecha2 - Segunda fecha en formato ISO.
 * @param {boolean} isPreview - Indica si se está en vista previa.
 * @returns {string|null} - Fecha más reciente o nulo si es vista previa.
 * @example
 * getMostRecentDate('2024-04-13T12:00:00Z', '2024-04-14T10:00:00Z', false); // "2024-04-14T10:00:00Z"
 */
export function getMostRecentDate(fecha1, fecha2, isPreview) {
  if (isPreview || (!fecha1 || !fecha2)) {
    return null
  }
  const partesFecha1 = fecha1.split(/[/\s:]/)
  const partesFecha2 = fecha2.split(/[/\s:]/)

  const [dia1, mes1, anio1, hora1, minutos1] = partesFecha1.map(Number)
  const [dia2, mes2, anio2, hora2, minutos2] = partesFecha2.map(Number)

  const fechaObjeto1 = new Date(anio1, mes1 - 1, dia1, hora1, minutos1)
  const fechaObjeto2 = new Date(anio2, mes2 - 1, dia2, hora2, minutos2)

  if (fechaObjeto1 > fechaObjeto2) {
    return fecha1
  } else if (fechaObjeto1 < fechaObjeto2) {
    return fecha2
  } else {
    return fecha1
  }
}

/**
 * Formatea la fecha y hora en formato "día/mes/año hora:minutos" o "hora:minuto" si es el mismo día.
 * @param {string} date - Fecha en formato ISO.
 * @returns {string} - Fecha y hora formateada.
 * @example
 * formatDateHour('2024-04-13T12:00:00Z'); // "13/04/2024 12:00"
 */
export function formatDateHour(date) {
  const currentDate = new Date(date)
  const today = new Date()
  today.setHours(0, 0, 0, 0)

  const isSameDay =
    currentDate.getDate() === today.getDate() &&
    currentDate.getMonth() === today.getMonth() &&
    currentDate.getFullYear() === today.getFullYear()

  if (isSameDay) {
    return currentDate.toLocaleTimeString('es-AR', {
      hour: '2-digit',
      minute: '2-digit',
      timeZone: 'America/Argentina/Buenos_Aires',
    })
  } else {
    return getMonthDayYear(currentDate)
  }
}

/**
 * Formatea una fecha y hora en formato GMT-3 (Argentina) a partir de una fecha dada en hora local de Argentina.
 * @param {string} argentinaDateTime - Fecha y hora en formato local de Argentina (ejemplo: "2024-04-19T10:40:00").
 * @returns {string} - Fecha y hora formateadas en formato GMT-3 (Argentina) en formato ISO8601.
 * @throws {Error} - Lanza un error si la fecha no puede ser formateada correctamente.
 */
export function formatToGMTFromArgentinaDateTime(argentinaDateTime) {
  try {
    const {
      withZero: { hour, minutes, month, year, day, seconds },
    } = getFormattedDate(argentinaDateTime)

    const dayNum = Number(day)
    const monthNum = Number(month)
    const yearNum = Number(year)
    const hourNum = Number(hour)
    const minutesNum = Number(minutes)
    const secondsNum = Number(seconds)

    if (
      isNaN(dayNum) ||
      isNaN(monthNum) ||
      isNaN(yearNum) ||
      isNaN(hourNum) ||
      isNaN(minutesNum) ||
      isNaN(secondsNum)
    ) {
      throw new Error('Fecha y hora no válidas.')
    }

    // Argentina está en GMT-3, por lo que para pasar a UTC hay que sumar 3 horas.
    // Se usa Date.UTC en vez de sumar/concatenar strings a mano
    const utcDate = new Date(
      Date.UTC(yearNum, monthNum - 1, dayNum, hourNum + 3, minutesNum, secondsNum),
    )

    if (isNaN(utcDate.getTime())) {
      throw new Error('Fecha y hora no válidas.')
    }

    return utcDate.toISOString()
  } catch (error) {
    console.error('Error al formatear la fecha:', error)
    return ''
  }
}


export const isValidDateForObjectDate = (dateString) => {
  const date = new Date(dateString);
  return !isNaN(date.getTime());
}

// Función para convertir la fecha en formato "Miércoles, 22 de Mayo de 2024" a "20240522"
export function convertDateToNumeric(dateString) {
  const meses = {
    "Enero": "01",
    "Febrero": "02",
    "Marzo": "03",
    "Abril": "04",
    "Mayo": "05",
    "Junio": "06",
    "Julio": "07",
    "Agosto": "08",
    "Septiembre": "09",
    "Octubre": "10",
    "Noviembre": "11",
    "Diciembre": "12",
  };

// Extraemos partes de la fecha
const partesFecha = dateString?.split(" ");
const dia = partesFecha?.[1]; 
const mes = partesFecha?.[3];
const año = partesFecha?.[5];

// Obtener el número del mes
const numeroMes = meses[mes];

  // Concatenamos el año, mes y día en el formato requerido

  const formattedDate = `${año}${numeroMes}${dia}`;

  return formattedDate;
}

/**
 * Convierte minutos a formato de horas y minutos (Ej: "1h 10min")
 * @param {number} totalMinutes - Total de minutos a convertir
 * @returns {string} - Tiempo formateado en formato "Xh Ymin"
 * @example
 * minutesToHoursAndMinutes(70); // "1h 10min"
 * minutesToHoursAndMinutes(45); // "45min"
 * minutesToHoursAndMinutes(120); // "2h"
 * minutesToHoursAndMinutes(150); // "2h 30min"
 */
export const minutesToHoursAndMinutes = (totalMinutes) => {
  if (isNaN(totalMinutes) || totalMinutes < 0) {
    return '';
  }

  const hours = Math.floor(totalMinutes / 60);
  const minutes = totalMinutes % 60;
  
  if (hours === 0) {
    return `${minutes}min`;
  } else if (minutes === 0) {
    return `${hours}h`;
  } else {
    return `${hours}h ${minutes}min`;
  }
};

/**
 * Obtiene el número de día del mes para un día específico de la semana actual
 * @param {string} dayName - Nombre del día ('lunes', 'martes', 'domingo', etc.)
 * @returns {number} - Número del día del mes
 * @example
 * getCurrentWeekDay('lunes');  // Si estamos en la semana del 4 al 11 de julio, retorna 4
 * getCurrentWeekDay('domingo'); // Si estamos en la semana del 4 al 11 de julio, retorna 11
 */
export const getCurrentWeekDay = (dayName) => {
  const days = {
    lunes: 1,
    martes: 2,
    miercoles: 3,
    jueves: 4,
    viernes: 5,
    sabado: 6,
    domingo: 0
  };
  
  const dayIndex = days[dayName.toLowerCase()];
  
  if (dayIndex === undefined) {
    return null;
  }
  
  const today = new Date();
  const currentDayOfWeek = today.getDay(); // 0 (domingo) a 6 (sábado)
  
  // Calcular la diferencia para llegar al lunes de esta semana
  const daysFromMonday = currentDayOfWeek === 0 ? 6 : currentDayOfWeek - 1;
  
  // Fecha del lunes de esta semana
  const monday = new Date(today);
  monday.setDate(today.getDate() - daysFromMonday);
  
  // Calcular la fecha del día solicitado
  const targetDate = new Date(monday);
  const daysToAdd = dayIndex === 0 ? 6 : dayIndex - 1; // Si es domingo, sumamos 6 días al lunes
  targetDate.setDate(monday.getDate() + daysToAdd);
  
  return targetDate.getDate();
};

export const getCurrentMonth = () => {
  const monthday = [
    'Enero',
    'Febrero',
    'Marzo',
    'Abril',
    'Mayo',
    'Junio',
    'Julio',
    'Agosto',
    'Septiembre',
    'Octubre',
    'Noviembre',
    'Diciembre',
  ]
  const today = new Date();
  return monthday[today.getMonth()];
}

/**
* Obtiene la fecha formateada que incluye día de la semana, día y mes.
*
* @param {Date} dateString - Objeto Date que representa la fecha.
* @returns {string} Cadena con la fecha formateada que incluye día de la semana, día y mes.
*/
export const getWeekDayDayMonthDay = (dateString) => {
  const weekDays = ['Domingo', 'Lunes', 'Martes', 'Miércoles', 'Jueves', 'Viernes', 'Sábado'];
  const months = ['enero', 'febrero', 'marzo', 'abril', 'mayo', 'junio', 'julio', 'agosto', 'septiembre', 'octubre', 'noviembre', 'diciembre'];

  // Crear la fecha en UTC
  const dateParts = dateString.split('-');
  const date = new Date(Date.UTC(dateParts[0], dateParts[1] - 1, dateParts[2]));

  const weekDay = weekDays[date.getUTCDay()];
  const day = date.getUTCDate();
  const month = months[date.getUTCMonth()];
  const year = date.getUTCFullYear();

  return {day, weekDay, month, year};
 };

/**
* Obtiene la hora formateada de una fecha con hora.
*
* @param {Date} date - Objeto Date que representa la fecha.
* @returns {string} Cadena con la hora formateada.
*/
export const getTimeFromDateTime = (dateTimeString) => {
  const date = new Date(dateTimeString);

  let hours = date.getHours();
  let minutes = date.getMinutes();

  hours = hours < 10 ? '0' + hours : hours;
  minutes = minutes < 10 ? '0' + minutes : minutes;

  return `${hours}:${minutes}`;
}

/** Retorna dos string con fecha y hora
 * el primero: "DD/MM"
 * el segungo: "HH:mm"
 * @param {string} date - "YYYY-MM-DD HH:mm:ss".
 * @returns {Array} - [fecha, hora]
 */
export const getDateAndHour = (date) => {
  if (!date) return ['', ''];
  const dateObj = new Date(date);
  const day = String(dateObj.getDate()).padStart(2, '0');
  const month = String(dateObj.getMonth() + 1).padStart(2, '0');
  const hour = String(dateObj.getHours()).padStart(2, '0');
  const minutes = String(dateObj.getMinutes()).padStart(2, '0');

  return [`${day}/${month}`, `${hour}:${minutes}`];
};

/**
 * Formatea una fecha ISO a dos strings separados: fecha y hora.
 *
 * El formato de salida es:
 *   - "DD.MM.YYYY - HH:mm"
 *
 * @param {string} isoString - Fecha en formato ISO (por ejemplo: "2025-07-22T14:15:00.552Z").
 * @returns {string} - Array con [fechaFormateada, horaFormateada]
 */
export const formatDateWithDot = (isoString) => {
  if (!isoString) return ['', ''];

  const date = new Date(isoString);
  const pad = (n) => n.toString().padStart(2, '0');

  const day = pad(date.getDate());
  const month = pad(date.getMonth() + 1);
  const year = date.getFullYear();
  const hours = pad(date.getHours());
  const minutes = pad(date.getMinutes());

  return `${day}.${month}.${year} - ${hours}:${minutes}`;
};

/**
 * Normaliza el nombre de un día de la semana y lo devuelve
 * correctamente acentuado en minúsculas.
 * @param {string} dayName - Nombre del día (ej: "miercoles", "Mié", "Miercóles,").
 * @returns {string} - Día normalizado en lowercase (ej: "miércoles"). Si no reconoce, devuelve el input trimmed en lowercase.
 */
export const normalizeWeekDay = (dayName) => {
  if (!dayName && dayName !== 0) return '';

  // Forzar string, quitar espacios y pasar a minúsculas
  const raw = String(dayName).trim().toLowerCase();

  // Normalizar para quitar diacríticos (acentos) y eliminar caracteres no alfabéticos
  const withoutAccents = raw.normalize('NFD').replace(/[\u0300-\u036f]/g, '');
  const cleaned = withoutAccents.replace(/[^a-zñ]/g, '');

  const daysMap = {
    lunes: 'lunes',
    martes: 'martes',
    miercoles: 'miércoles',
    jueves: 'jueves',
    viernes: 'viernes',
    sabado: 'sábado',
    domingo: 'domingo',
  };

  if (daysMap[cleaned]) return daysMap[cleaned];

  // Intento de coincidencia por prefijo (acepta 'mie', 'mié', 'miér', etc.)
  const foundKey = Object.keys(daysMap).find((k) => k.startsWith(cleaned) || cleaned.startsWith(k));
  if (foundKey) return daysMap[foundKey];

  // Si no se reconoce, devolver la versión trimmed en lowercase
  return raw;
};

/**
 * Reemplaza el día de la semana dentro de una frase por su forma normalizada.
 * Las palabras que no son días se devuelven tal cual están.
 * @param {string} text - Frase que puede contener un día (ej: "Las mañanas de los miercoles").
 * @returns {string} - Frase con el día normalizado (ej: "Las mañanas de los miércoles").
 */
export const replaceWeekDayInText = (text) => {
  if (!text && text !== 0) return '';

  const daysWithoutAccents = ['lunes', 'martes', 'miercoles', 'jueves', 'viernes', 'sabado', 'domingo'];

  // Convertir text a array de strings (palabras)
  const words = String(text).split(' ');

  // Mapear cada palabra
  const result = words.map((word) => {
    // Extraer solo letras para comparar (sin acentos ni signos)
    const cleanWord = word
      .normalize('NFD')
      .replace(/[\u0300-\u036f]/g, '')
      .replace(/[^a-zñA-ZÑ]/g, '')
      .toLowerCase();

    // Si es un día, normalizarlo preservando signos de puntuación
    if (daysWithoutAccents.includes(cleanWord)) {
      // Obtener caracteres antes y después de la palabra
      const leadingMatch = word.match(/^(\W*)/);
      const trailingMatch = word.match(/(\W*)$/);
      const leading = leadingMatch ? leadingMatch[1] : '';
      const trailing = trailingMatch ? trailingMatch[1] : '';

      const normalizedDay = normalizeWeekDay(cleanWord);
      return leading + normalizedDay + trailing;
    }

    // Si no es un día, devolver la palabra tal cual
    return word;
  });

  return result.join(' ');
};